/**
 * LRC 文件格式生成和解析工具
 */

/**
 * 將秒數轉換為 LRC 時間格式 [mm:ss.xx]
 */
export const formatLRCTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(2);
    const paddedSecs = secs.padStart(5, '0');
    return `[${minutes.toString().padStart(2, '0')}:${paddedSecs}]`;
};

/**
 * 生成 LRC 文件內容
 */
export const generateLRC = ({ lyrics = [], title = '', artist = '', album = '' }) => {
    let lrc = '';
    
    if (title) lrc += `[ti:${title}]\n`;
    if (artist) lrc += `[ar:${artist}]\n`;
    if (album) lrc += `[al:${album}]\n`;
    if (title || artist || album) lrc += '\n';
    
    const sortedLyrics = [...lyrics].sort((a, b) => a.time - b.time);
    
    sortedLyrics.forEach(({ time, text }) => {
        lrc += `${formatLRCTime(time)}${text}\n`;
    });
    
    return lrc;
};

/**
 * 下載 LRC 文件
 */
export const downloadLRC = (lrcContent, filename = 'lyrics') => {
    const blob = new Blob([lrcContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.lrc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * 使用 OpenAI Whisper API 進行語音識別和歌詞對齊
 * 需要 OpenAI API Key
 */
export const generateLRCWithWhisper = async (audioFile, lyricsText, apiKey) => {
    try {
        // Step 1: 使用 Whisper API 進行語音識別
        const formData = new FormData();
        formData.append('file', audioFile);
        formData.append('model', 'whisper-1');
        formData.append('response_format', 'verbose_json');
        formData.append('timestamp_granularities[]', 'word');

        const transcriptionResponse = await fetch('https://api.openai.com/v1/audio/transcriptions', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
            },
            body: formData
        });

        if (!transcriptionResponse.ok) {
            throw new Error(`Whisper API error: ${transcriptionResponse.statusText}`);
        }

        const transcription = await transcriptionResponse.json();
        
        // Step 2: 將歌詞文本分割成行
        const lyricsLines = lyricsText.split('\n').filter(line => line.trim());
        
        // Step 3: 使用簡單的文本匹配算法對齊歌詞
        // 這裡可以使用更複雜的算法，比如動態時間規整 (DTW)
        const alignedLyrics = alignLyricsWithTranscription(lyricsLines, transcription.words || []);
        
        return alignedLyrics;
    } catch (error) {
        console.error('Error generating LRC with Whisper:', error);
        throw error;
    }
};

/**
 * 驗證 OpenAI API Key 是否有效
 * 這個函式會呼叫一個輕量級的 OpenAI endpoint（/v1/models）
 * 只用來確認 key 是否有效，不會產生額外的費用型推論請求。
 */
export const validateOpenAIKey = async (apiKey) => {
    try {
        if (!apiKey || !apiKey.trim()) {
            throw new Error('請先輸入 API Key');
        }

        const response = await fetch('https://api.openai.com/v1/models', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('API Key 無效或權限不足（401 Unauthorized）');
            }
            throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
        }

        // 回傳 true 代表 key 看起來是有效的
        return true;
    } catch (error) {
        console.error('Error validating OpenAI API key:', error);
        throw error;
    }
};

/**
 * 對齊歌詞文本和語音識別結果
 */
const alignLyricsWithTranscription = (lyricsLines, words) => {
    const alignedLyrics = [];
    let wordIndex = 0;
    
    for (const line of lyricsLines) {
        if (wordIndex >= words.length) break;
        
        // 簡單的匹配：找到包含歌詞行中關鍵詞的時間戳
        const lineWords = line.toLowerCase().split(/\s+/).filter(w => w.length > 2);
        let bestMatch = null;
        let bestScore = 0;
        
        // 在語音識別結果中搜索匹配
        for (let i = wordIndex; i < Math.min(wordIndex + 50, words.length); i++) {
            let score = 0;
            for (const lineWord of lineWords) {
                if (words[i]?.word?.toLowerCase().includes(lineWord)) {
                    score++;
                }
            }
            if (score > bestScore) {
                bestScore = score;
                bestMatch = i;
            }
        }
        
        if (bestMatch !== null && words[bestMatch]) {
            const time = words[bestMatch].start;
            alignedLyrics.push({ time, text: line });
            wordIndex = bestMatch + 1;
        } else {
            // 如果找不到匹配，使用前一個時間戳 + 估計的間隔
            const lastTime = alignedLyrics.length > 0 
                ? alignedLyrics[alignedLyrics.length - 1].time 
                : 0;
            alignedLyrics.push({ time: lastTime + 3, text: line });
        }
    }
    
    return alignedLyrics;
};

/**
 * 使用簡單的時間估算方法生成 LRC（不需要 API）
 * 根據音頻時長和歌詞行數平均分配時間
 */
export const generateLRCWithTimeEstimation = async (audioFile, lyricsText) => {
    return new Promise((resolve, reject) => {
        const audio = new Audio(URL.createObjectURL(audioFile));
        
        audio.onloadedmetadata = () => {
            const duration = audio.duration;
            const lyricsLines = lyricsText.split('\n').filter(line => line.trim());
            
            if (lyricsLines.length === 0) {
                reject(new Error('歌詞文本為空'));
                return;
            }

            // 平均分配時間，每行歌詞的時間間隔
            const timePerLine = duration / lyricsLines.length;
            const alignedLyrics = lyricsLines.map((line, index) => ({
                time: index * timePerLine,
                text: line
            }));

            // 清理音頻 URL
            URL.revokeObjectURL(audio.src);
            resolve(alignedLyrics);
        };

        audio.onerror = () => {
            reject(new Error('無法讀取音頻文件'));
        };
    });
};

/**
 * 使用 Web Speech API 進行語音識別（瀏覽器內置，但功能有限）
 * 注意：Web Speech API 需要從麥克風輸入，無法直接處理音頻文件
 * 此方法僅作為備選方案，建議使用 Whisper API
 */
export const generateLRCWithWebSpeech = (audioFile, lyricsText) => {
    return new Promise((resolve, reject) => {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            reject(new Error('Web Speech API 不支持：您的瀏覽器不支持語音識別功能。建議使用 OpenAI Whisper API 或使用時間估算方法。'));
            return;
        }

        // Web Speech API 無法直接處理音頻文件，需要從麥克風輸入
        // 這是一個限制，所以我們建議使用時間估算方法
        reject(new Error('Web Speech API 限制：此 API 需要從麥克風實時輸入，無法直接處理音頻文件。建議使用 OpenAI Whisper API（更準確）或使用時間估算方法（簡單快速）。'));
    });
};

